import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { getBlogPost } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-24 text-center min-h-screen">
        <h1 className="font-serif text-4xl text-foreground">Essay not found</h1>
        <Link to="/blog" className="mt-6 inline-block text-gold hover:text-gold-light">← Back to Journal</Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-40 pb-24 flex justify-center min-h-screen">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  const paragraphs = (post.content || "").split("\n").filter(Boolean);

  return (
    <article className="pt-32 pb-24 min-h-screen" data-testid="blog-post-page">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-10">
          <ArrowLeft size={16} /> Back to Journal
        </Link>
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-gold/80 mb-5">
          <span>{post.category}</span><span>·</span><span>{post.read_time}</span><span>·</span><span>{post.published_at}</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="font-serif text-4xl md:text-6xl font-light leading-[1.05] text-foreground">
          {post.title}
        </motion.h1>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1 }}
        className="max-w-5xl mx-auto px-6 mt-12">
        <div className="relative h-[320px] md:h-[460px] rounded-3xl overflow-hidden border border-gold/15">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
        </div>
      </motion.div>

      <div className="max-w-3xl mx-auto px-6 mt-14 space-y-7">
        {paragraphs.map((p, i) => (
          <p key={i} className={`text-lg md:text-xl text-foreground/85 font-light leading-relaxed ${i === 0 ? "first-letter:text-6xl first-letter:font-serif first-letter:text-gold first-letter:mr-3 first-letter:float-left first-letter:leading-[0.8]" : ""}`}>
            {p}
          </p>
        ))}

        {post.tags && (
          <div className="flex flex-wrap gap-3 pt-8 border-t border-gold/10">
            {post.tags.map((t) => (
              <span key={t} className="px-4 py-1.5 rounded-full border border-gold/25 text-xs uppercase tracking-widest text-gold/80">{t}</span>
            ))}
          </div>
        )}

        <div className="pt-10">
          <Link to="/book" data-testid="post-book-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors">
            Work with me <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </article>
  );
}
